import React, { useCallback, useEffect, useState } from "react";
import {
  Col,
  Logo,
  UiButton,
  UiCenter,
  UiHStack,
  UiImage,
  UiPinInput,
  UiPinInputField,
  UiPrimaryButton,
  UiText,
} from "~/components/elements";
import mailVerifyCode from "~/assets/img/v2/mail_verify_code.png";
import { useAppDispatch, useInput, useNotification, useRequestState } from "~/hooks";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";
import { useHistory } from "react-router-dom";
import {
  sendMailResetPwd,
  sendMailVerification,
  verifyEmailCode,
  verifyEmailCodeMisc,
  verifyPasscodeResetPwd,
} from "~/redux/auth/thunk";

export function ConfirmVerificationCode({
  onSuccess,
  userId,
  email,
  type,
}: {
  onSuccess: ({ code }: { code: string }) => void;
  userId: string;
  email: string;
  type: "resetPassword" | "register";
}) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { addMessage } = useNotification(dispatch);

  const [seconds, setSeconds] = useState(60);
  const { loading: verifyEmailCodeLoading } = useRequestState(verifyEmailCode);
  const { loading: sendMailVerificationLoading } = useRequestState(sendMailVerification);
  const { loading: verifyPasscodeResetPwdLoading } = useRequestState(verifyPasscodeResetPwd);
  const { loading: sendMailResetPwdLoading } = useRequestState(sendMailResetPwd);

  const isLoading =
    verifyEmailCodeLoading || sendMailVerificationLoading || verifyPasscodeResetPwdLoading || sendMailResetPwdLoading;

  const rules = { code: { presence: true } };
  const [inputs, setInputs, { validation }] = useInput({ code: "" }, { rules });

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [seconds]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault();
      if (validation.getErrors()) {
        return;
      }
      if (type === "resetPassword") {
        const resAction = await dispatch(verifyPasscodeResetPwd({ data: { code: inputs.code, uid: userId } }));
        if (requestOK(resAction, verifyPasscodeResetPwd)) {
          unwrapResult(resAction);
          onSuccess({ code: inputs.code });
        }
      }

      if (type === "register") {
        await dispatch(verifyEmailCodeMisc({ data: { code: inputs.code, uid: userId } }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputs.code]
  );

  useEffect(() => {
    if (inputs.code?.length === 6) {
      // @ts-ignore
      handleSubmit(undefined);
    }
  }, [inputs.code, handleSubmit, dispatch, history, type, userId]);

  const handleResend = async (e: React.FormEvent) => {
    e?.preventDefault();
    setInputs({ code: "" });
    if (type === "resetPassword") {
      const resAction = await dispatch(sendMailResetPwd({ data: { email } }));
      if (requestOK(resAction, sendMailResetPwd)) {
        onResendSuccess();
      }
    }
    if (type === "register") {
      const resAction = await dispatch(sendMailVerification({ data: { email } }));
      if (requestOK(resAction, sendMailVerification)) {
        onResendSuccess();
      }
    }
  };

  const onResendSuccess = () => {
    setSeconds(60);
    addMessage(`We've resent a verification code.`, "success");
  };

  return (
    <Col mt={8} px={{ base: 4 }} maxW="29.625rem" w="full">
      <Logo containerProps={{ justifyContent: "center" }} />
      <UiCenter flexDirection="column" flex={1} px={3} mt={12}>
        <UiImage src={mailVerifyCode} width="5rem" />
        <UiText vr="headline3" fontSize={{ base: "1.5rem", md: "1.75rem" }} mt={4}>
          Confirm verification code
        </UiText>
        <UiText vr="body1" textAlign="center" mt={4}>
          We&apos;ve sent a verification code to&nbsp;
          <UiText vr="body1" as="span" color="blue">
            {email}
          </UiText>
          .&nbsp;Please check your email.
        </UiText>
        <UiHStack mt={6} w="full" justifyContent="space-between">
          <UiPinInput
            type="alphanumeric"
            size="lg"
            placeholder=""
            onChange={value => setInputs({ code: value })}
            value={inputs.code}
          >
            <UiPinInputField
              bg="white"
              border="none"
              minW="50px"
              w="full"
              aspectRatio="1/1.08"
              height="auto"
              textTransform="uppercase"
              fontWeight="bold"
            />
            <UiPinInputField
              bg="white"
              border="none"
              minW="50px"
              w="full"
              aspectRatio="1/1.08"
              height="auto"
              textTransform="uppercase"
              fontWeight="bold"
            />
            <UiPinInputField
              bg="white"
              border="none"
              minW="50px"
              w="full"
              aspectRatio="1/1.08"
              height="auto"
              textTransform="uppercase"
              fontWeight="bold"
            />
            <UiPinInputField
              bg="white"
              border="none"
              minW="50px"
              w="full"
              aspectRatio="1/1.08"
              height="auto"
              textTransform="uppercase"
              fontWeight="bold"
            />
            <UiPinInputField
              bg="white"
              border="none"
              minW="50px"
              w="full"
              aspectRatio="1/1.08"
              height="auto"
              textTransform="uppercase"
              fontWeight="bold"
            />
            <UiPinInputField
              bg="white"
              border="none"
              minW="50px"
              w="full"
              aspectRatio="1/1.08"
              height="auto"
              textTransform="uppercase"
              fontWeight="bold"
            />
          </UiPinInput>
        </UiHStack>

        <UiPrimaryButton
          isLoading={isLoading}
          isDisabled={inputs.code?.length !== 6}
          type="submit"
          onClick={handleSubmit}
          mt={6}
          w="full"
        >
          Confirm
        </UiPrimaryButton>
        <UiText vr="body2" mx="auto" mt={3}>
          Did not receive the code yet?&nbsp;
          {!seconds && (
            <UiButton
              fontSize="0.875rem"
              variant="link"
              isLoading={isLoading}
              onClick={handleResend}
              isDisabled={seconds > 0}
            >
              Resend code
            </UiButton>
          )}
        </UiText>
        {seconds > 0 && (
          <UiText vr="body2" mx="auto" mt={1} color="navy">
            Resend code in {seconds} seconds
          </UiText>
        )}
      </UiCenter>
    </Col>
  );
}
